import PropTypes from 'prop-types';
import React from 'react';
import Relay from 'react-relay/classic';
import DocumentTitle from 'react-document-title';
import { Link } from 'react-router';

import Button from 'app/components/shared/Button';
import Icon from 'app/components/shared/Icon';
import Panel from 'app/components/shared/Panel';
import PageHeader from 'app/components/shared/PageHeader';
import Spinner from 'app/components/shared/Spinner';

class SSOIndex extends React.PureComponent {
  static propTypes = {
    organization: PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      sso: PropTypes.object,
      ssoProviders: PropTypes.object
    }).isRequired,
    relay: PropTypes.object.isRequired
  };

  componentDidMount() {
    this.props.relay.forceFetch({ isMounted: true });
  }

  render() {
    return (
      <DocumentTitle title={`SSO · ${this.props.organization.name}`}>
        <div>
          <PageHeader>
            <PageHeader.Icon>
              <Icon
                icon="sso"
                style={{ width: 40, height: 40 }}
              />
            </PageHeader.Icon>
            <PageHeader.Title>
              Single Sign On
            </PageHeader.Title>
            <PageHeader.Description>
              Manage your organization’s Single Sign On settings
            </PageHeader.Description>
          </PageHeader>

          {this.renderDetailsPanel()}
        </div>
      </DocumentTitle>
    );
  }

  renderLoginLink() {
    const url = `${window.location.origin}/login?org=${this.props.organization.slug}`;

    return (
      <a
        href={url}
        className="lime text-decoration-none hover-lime hover-underline"
        onClick={this.handleLoginLinkClick}
      >
        {url}
      </a>
    );
  }

  renderSSO2LoginLink() {
    const url = `${window.location.origin}/sso/${this.props.organization.slug}`;

    return (
      <a
        href={url}
        className="lime text-decoration-none hover-lime hover-underline"
      >
        {url}
      </a>
    );
  }

  handleLoginLinkClick(evt) {
    evt.preventDefault();
    alert('You’re already logged in! You can copy the link from here to share it.');
  }

  renderEmailURI() {
    const { organization } = this.props;

    const address = 'support@buildkite.com';
    const subject = (
      `Please enable SSO for ${organization.name} (“${organization.slug}”)`
    );

    return `mailto:${address}?subject=${encodeURIComponent(subject)}`;
  }

  renderDetailsPanel() {
    if (!this.props.organization.sso) {
      return (
        <Panel>
          <Panel.Section className="center">
            <Spinner />
          </Panel.Section>
        </Panel>
      );
    }

    const enabledSSO2ProviderEdge = this.props.organization.ssoProviders.edges.find((providerEdge) => {
      if (providerEdge.node.state === "ENABLED") {
        return providerEdge.node;
      }
    });

    if (enabledSSO2ProviderEdge) {
      const sso2Provider = enabledSSO2ProviderEdge.node;

      // Prefer the SAML provider name, otherwise come up with a decent default
      let providerName;
      if (sso2Provider.identityProvider && sso2Provider.identityProvider.name) {
        providerName = sso2Provider.identityProvider.name;
      } else if (sso2Provider.__typename === "SSOProviderGoogleGSuite") {
        providerName = "Google GSuite";
      } else if (sso2Provider.__typename === "SSOProviderGitHubAppType") {
        providerName = "GitHub";
      } else if (sso2Provider.__typename === "SSOProviderSAML") {
        providerName = "SAML";
      } else {
        providerName = "SSO";
      }

      return (
        <Panel>
          <Panel.Section className="max-width-3">
            <p className="h4 bold">Single Sign On is enabled for this organization.</p>
            <p>Your organization members can login via {providerName}, or by using the following link, which will redirect them to your provider for authorization:</p>
            <p>{this.renderSSO2LoginLink()}</p>
            <p>If the member’s email address is not found, a new member will provisioned in your organization. You can also manually send them an <Link to={`/organizations/${this.props.organization.slug}/users`} className="lime text-decoration-none hover-lime hover-underline">invitation to join</Link>.</p>
            <p>SSO authorization will be required before accessing any organization data. You can also modify each user’s SSO permissions to make SSO login optional, in case of emergency if your SSO provider is unavailable, or for bot users.</p>
            <p>See the <a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso">SSO documentation</a> for more information.</p>
          </Panel.Section>
        </Panel>
      );
    }

    if (this.props.organization.sso.isEnabled) {
      return (
        <Panel>
          <Panel.Section className="max-width-3">
            <p className="h4 bold">Single Sign On is enabled for this organization.</p>
            <p>To login, users can enter their their email address on the Buildkite login page and they will redirected to {this.props.organization.sso.provider.name} for authentication and sign-in.</p>
            <p>If you have multiple organizations configured with SSO, users can login using this organization-specific login URL:<br />{this.renderLoginLink()}</p>
          </Panel.Section>
        </Panel>
      );
    }

    return (
      <div>
        <Panel>
          <Panel.Section className="max-width-3">
            <p>Single Sign On (SSO) allows you to use your own authentication server for signing into Buildkite.</p>
            <p>For FAQs and more information, see the <a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso">Introduction to SSO with Buildkite</a> guide.</p>
            <p>Supported SSO providers:</p>
            <ul>
              <li>Google G Suite (<a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso/google-oauth">Instructions</a>)</li>
              <li>Okta (<a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso/okta">Instructions</a>)</li>
              <li>OneLogin (<a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso/onelogin">Instructions</a>)</li>
              <li>ADFS (<a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso/adfs">Instructions</a>)</li>
              <li>Any other provider that supports SAML 2.0 (<a className="lime text-decoration-none hover-lime hover-underline" href="https://buildkite.com/docs/integrations/sso/custom-saml">Instructions</a>)</li>
            </ul>
            <p>To enable SSO for this organization, contact support with the details of your authentication server. The required information for each provider can be found in the guides above.</p>
            <Button href={this.renderEmailURI()}>
              Contact Support
            </Button>
          </Panel.Section>
        </Panel>
      </div>
    );
  }
}

export default Relay.createContainer(SSOIndex, {
  initialVariables: {
    isMounted: false
  },

  fragments: {
    organization: () => Relay.QL`
      fragment on Organization {
        name
        slug
        ssoProviders(first: 5) @include(if: $isMounted) {
          edges {
            node {
              state
              __typename
              ...on SSOProviderSAML {
                identityProvider {
                  name
                }
              }
            }
          }
        }
        sso @include(if: $isMounted) {
          isEnabled
          provider {
            name
          }
        }
      }
    `
  }
});
